import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MANDANT_TYPE_LABELS } from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { Spinner } from '../../components/ui/Spinner';
import { useDeactivateMandant, useMandant, useUpdateMandant } from './hooks';
import { MandantForm } from './MandantForm';

function Field({ label, value }: { label: string; value?: string | null }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-800">{value && value.length > 0 ? value : '—'}</dd>
    </div>
  );
}

export function MandantDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const query = useMandant(id);
  const updateMandant = useUpdateMandant(id ?? '');
  const deactivateMandant = useDeactivateMandant(id ?? '');
  const [editing, setEditing] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  if (query.isLoading) {
    return (
      <div className="py-10 text-center">
        <Spinner label="Chargement du mandant…" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="space-y-3">
        <Alert variant="error">{toDisplayError(query.error).message}</Alert>
        <Link to="/mandants" className="text-sm text-brand-600 hover:underline">
          ← Retour à la liste
        </Link>
      </div>
    );
  }

  const mandant = query.data;

  return (
    <div className="max-w-3xl space-y-4">
      <Link to="/mandants" className="text-sm text-brand-600 hover:underline">
        ← Retour à la liste
      </Link>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{mandant.nom}</CardTitle>
            {mandant.isActive ? (
              <Badge tone="success">Actif</Badge>
            ) : (
              <Badge tone="neutral">Inactif</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {updateMandant.isError ? (
            <Alert variant="error" details={toDisplayError(updateMandant.error).details}>
              {toDisplayError(updateMandant.error).message}
            </Alert>
          ) : null}
          {deactivateMandant.isError ? (
            <Alert variant="error">{toDisplayError(deactivateMandant.error).message}</Alert>
          ) : null}

          {editing ? (
            <MandantForm
              initial={mandant}
              submitLabel="Enregistrer"
              pending={updateMandant.isPending}
              onCancel={() => setEditing(false)}
              onSubmit={(input) =>
                updateMandant.mutate(input, { onSuccess: () => setEditing(false) })
              }
            />
          ) : (
            <>
              <dl className="grid gap-4 sm:grid-cols-2">
                <Field label="Type" value={MANDANT_TYPE_LABELS[mandant.type]} />
                <Field label="Téléphone" value={mandant.coordonnees?.telephone} />
                <Field label="E-mail" value={mandant.coordonnees?.email} />
                <Field label="Contact" value={mandant.coordonnees?.contact} />
                <Field label="Adresse" value={mandant.coordonnees?.adresse} />
              </dl>
              <div className="flex gap-2">
                <Button onClick={() => setEditing(true)}>Modifier</Button>
                {mandant.isActive ? (
                  <Button variant="destructive" onClick={() => setConfirmDeactivate(true)}>
                    Désactiver
                  </Button>
                ) : null}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDeactivate}
        onClose={() => setConfirmDeactivate(false)}
        onConfirm={() =>
          deactivateMandant.mutate(undefined, {
            onSuccess: () => setConfirmDeactivate(false),
          })
        }
        title="Désactiver le mandant"
        description={`Confirmez-vous la désactivation de « ${mandant.nom} » ? Cette action est sensible.`}
        confirmLabel="Désactiver"
        destructive
        loading={deactivateMandant.isPending}
      />
    </div>
  );
}
